import { createCdfEntry } from 'features/dataLayers/customCdfs/cdfGenerator'
import {
  CacheableRolodexEntryJson,
  GeckoAggregation,
  GeckoAlgorithm,
  GeckoAnglePlotSetting,
  GeckoBinSize,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoMeasurementUnit,
  GeckoSource,
  GeckoView,
  RolodexEntryType,
} from 'types'
import { formatDateLikeInspectionData } from 'utils/dateHelpers'
import { snapToNearestMultiple, SnapType } from 'utils/math'

type CreateReferenceNominalCdfProps = {
  generation: number
  referenceCdf: GeckoComponentDataFrame
}

type CreateGeneratedNominalCdfProps = {
  binSize: GeckoBinSize
  nominalDate: Date
  componentModel: GeckoComponentModel
  generation: number
  tags: GeckoComponentDataFrame['tags']
  referenceCdf?: undefined
}

type CreateNominalCdfProps =
  | CreateReferenceNominalCdfProps
  | CreateGeneratedNominalCdfProps

export function createNominalCdfEntry(
  props: CreateNominalCdfProps
): GeckoComponentDataFrame {
  if (props.referenceCdf) {
    const { generation, referenceCdf } = props
    const cdfEntry: CacheableRolodexEntryJson = {
      isFromCache: true,
      ...createCdfEntry(`nominal-${referenceCdf.id}`, {
        generation,
        geometry: referenceCdf.data.geometry,
        binning: referenceCdf.data.binning,
        view: GeckoView.Thickness,
        source: GeckoSource.Nominal,
        measurementUnit: GeckoMeasurementUnit.Millimeters,
        algorithm: GeckoAlgorithm.None,
        aggregation: GeckoAggregation.None,
        anglePlotSetting: GeckoAnglePlotSetting.None,
        tags: referenceCdf.tags,
      }),
    }
    return GeckoComponentDataFrame.CreateFromJSON(cdfEntry)
  }

  const { binSize, nominalDate, componentModel, generation, tags } = props

  const geometryXStart =
    componentModel.type === GeckoComponentModelType.LegacyBoilerWalls
      ? componentModel.componentBounds.start.x
      : componentModel.componentBounds.start.x.minus(binSize.width.scale(0.5))
  const geometryYStart = componentModel.componentBounds.start.y.minus(
    binSize.height.scale(0.5)
  )
  const startX = snapToNearestMultiple(
    geometryXStart.toNumber(binSize.width.unit),
    binSize.width.num / 2,
    SnapType.Floor
  )
  const startY = snapToNearestMultiple(
    geometryYStart.toNumber(binSize.height.unit),
    binSize.height.num / 2,
    SnapType.Floor
  )
  const endX =
    snapToNearestMultiple(
      componentModel.componentBounds.end.x.toNumber(binSize.width.unit) -
        startX,
      binSize.width.num,
      SnapType.Ceil
    ) + startX
  const endY =
    snapToNearestMultiple(
      componentModel.componentBounds.end.y.toNumber(binSize.height.unit) -
        startY,
      binSize.height.num,
      SnapType.Ceil
    ) + startY
  const id = `nominal-${binSize.id}-${formatDateLikeInspectionData(nominalDate)}-${tags[RolodexEntryType.COMPONENT_DATASET]}`

  return GeckoComponentDataFrame.CreateFromJSON({
    ...createCdfEntry(id, {
      generation,
      geometry: GeckoComponentRect.CreateFromXYs(
        tags.component,
        startX,
        startY,
        endX,
        endY,
        binSize.width.unit,
        binSize.height.unit
      ),
      binning: {
        offset: GeckoComponentCoords.CreateFromValues(
          tags.component,
          0,
          binSize.width.unit,
          0,
          binSize.height.unit
        ),
        bin_size: binSize,
        x_bin_count: Math.round((endX - startX) / binSize.width.num),
        y_bin_count: Math.round((endY - startY) / binSize.height.num),
      },
      view: GeckoView.Thickness,
      source: GeckoSource.Nominal,
      measurementUnit: GeckoMeasurementUnit.Millimeters,
      algorithm: GeckoAlgorithm.None,
      aggregation: GeckoAggregation.None,
      anglePlotSetting: GeckoAnglePlotSetting.None,
      tags,
    }),
    access_restriction: null,
  })
}
